import React from 'react';
import { arcPath } from '../../../../utils/clockGeometry';
import type { RoutineArcSegment } from '../../../../utils/routineArc';
import type { RoutineTimerArc } from '../../../../stores/progressDisplayStore';

interface RoutineArcProps {
  /** Which arc variant to draw (user setting). */
  style: RoutineTimerArc;
  /** Accent colour for the current step (the 'step' ring + 'layered' inner ring). */
  color: string;
  /** Elapsed fraction [0,1] of the current step (drives the 'step'/'layered' fill). */
  stepFraction: number;
  /** Per-step wedges for the 'routine' + 'layered' styles. */
  segments?: RoutineArcSegment[];
  /** Center content (countdown + XP). */
  children?: React.ReactNode;
  /** Rendered px size of the square arc. */
  size?: number;
}

// A near-full sweep can't be drawn as an arc path (endpoints coincide) — fall back
// to a full circle so a 100%-elapsed step still reads as a complete ring.
const FULL = 0.9999;
// Small angular gap (as a ring fraction) carved off each wedge so they read apart.
const GAP = 0.006;

/**
 * The radial arc on the active routine timer. One component, three looks (chosen
 * in Settings → Routine timer arc): a single current-step ring, the whole routine
 * as domain-coloured wedges, or both layered. Pure presentation — all progress
 * math comes in via props (see utils/routineArc).
 */
export function RoutineArc({ style, color, stepFraction, segments = [], children, size = 240 }: RoutineArcProps) {
  const frac = Math.min(1, Math.max(0, stepFraction));

  // A single progress ring (used by 'step' and as the inner ring of 'layered').
  const stepRing = (r: number, width: number) => (
    <>
      <circle cx={50} cy={50} r={r} fill="none" stroke={color} strokeOpacity={0.14} strokeWidth={width} />
      {frac >= FULL ? (
        <circle cx={50} cy={50} r={r} fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" />
      ) : frac > 0.0005 ? (
        <path
          d={arcPath(0, frac, r)}
          fill="none"
          stroke={color}
          strokeWidth={width}
          strokeLinecap="round"
          className="transition-all duration-700"
        />
      ) : null}
    </>
  );

  // The routine wedges (used by 'routine' and as the outer ring of 'layered').
  const wedges = (r: number, width: number) =>
    segments.map((s) => {
      const a = s.start + GAP;
      const b = s.start + s.sweep - GAP;
      if (b <= a) return null;
      const fillEnd = a + (b - a) * Math.min(1, Math.max(0, s.fill));
      return (
        <g key={s.index}>
          {/* dim wedge track */}
          <path d={arcPath(a, b, r)} fill="none" stroke={s.color} strokeOpacity={0.16} strokeWidth={width} />
          {/* elapsed portion */}
          {s.fill > 0.001 && (
            <path
              d={arcPath(a, fillEnd, r)}
              fill="none"
              stroke={s.color}
              strokeWidth={width}
              strokeLinecap="round"
              className="transition-all duration-700"
            />
          )}
        </g>
      );
    });

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full -rotate-0">
        {style === 'step' && stepRing(44, 7)}
        {style === 'routine' && wedges(44, 9)}
        {style === 'layered' && (
          <>
            {wedges(46, 4)}
            {stepRing(36, 7)}
          </>
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
}
